import React, { useEffect, useState } from 'react';
import { Box, Typography, Dialog, IconButton, Grid, InputAdornment, Divider } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { makeStyles } from '@mui/styles';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  emailValidation,
  isContainsLowercase,
  msisdnValidation,
  validateDigit,
  validateSpecialChar,
} from '../../../utils/validation';
import CustomButton from '../../UI/CustomButton';
import CustomInput, { typeTextField } from '../../UI/CustomInput';
import { addUser, bulkCreate, updateUser } from '../../../apis/user';
import { UserModel } from '../../../types/User';
import { setSnackbar } from '../../../store/common/common.reducer';
import { useDispatch } from 'react-redux';
import './add.scss';
import UserBulkAccording from './According';

const useStyles = makeStyles(() => ({
  paper: {
    direction: 'rtl',
    borderRadius: '1rem !important',
  },
  accordingRoot: {
    backgroundColor: 'transparent',
    boxShadow: 'none !important',
    border: '1px solid #DCDFE1 !important',
    borderRadius: '0.5rem !important',
  },
}));

interface ConfirmStartTestProps {
  openAddUserDialog: boolean;
  closeDialog: () => void;
  getAllUsers: () => void;
  selectUser: UserModel;
  setErrData: (err: any) => void;
}

const AddUserDialog: React.FC<ConfirmStartTestProps> = (props) => {
  const { openAddUserDialog, closeDialog, getAllUsers, selectUser, setErrData } = props;
  const styles = useStyles();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');

  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [msisdn, setMsisdn] = useState('');
  const [msisdnError, setMsisdnError] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [type, setType] = useState<typeTextField>('password');
  const [file, setFile] = useState<any>(null);
  const [fileLoading, setFileLoading] = useState(false);

  const clearAllState = () => {
    setName('');
    setLastName('');
    setEmail('');
    setMsisdn('');
    setPassword('');
    if (file) setFile(null);
    if (passwordError) setPasswordError(false);
    if (msisdnError) setMsisdnError(false);
  };

  const onCloseAndResetDialog = () => {
    clearAllState();
    closeDialog();
  };

  const onSubmitFile = () => {
    setFileLoading(true);
    let formData = new FormData();
    formData.append('file', file);
    bulkCreate(formData)
      .then((res: any) => {
        if (res?.HasError) setErrData(res);
        else
          dispatch(
            setSnackbar({
              openSnackbar: true,
              message: 'همه کاربران به صورت دسته ایی با موفقیت اضافه گردید',
            }),
          );
        getAllUsers();
        onCloseAndResetDialog();
      })
      .finally(() => setFileLoading(false));
  };

  useEffect(() => {
    if (selectUser && openAddUserDialog) {
      const { name, lastName, email, mobileNumber, password } = selectUser;
      const formatMsisdn = `0${mobileNumber?.slice(3, mobileNumber.toString()?.length)}`;

      setName(name);
      setLastName(lastName);
      if (email) setEmail(email);
      setMsisdn(formatMsisdn);
      if (password) setPassword(password);
    }
  }, [selectUser, openAddUserDialog]);

  const onChangePasswordHandler = (value: any) => {
    setPassword(value);
  };

  const onSubmit = () => {
    const { id } = selectUser || {};
    setLoading(true);

    const mobileNumber = msisdn.startsWith('0')
      ? `+98${msisdn.slice(1, msisdn.length)}`
      : `+98${msisdn}`;

    if (selectUser) {
      updateUser(id, name, lastName, email, password, mobileNumber)
        .then(() => {
          getAllUsers();
          closeDialog();
          dispatch(setSnackbar({ openSnackbar: true, message: 'کاربر با موفقیت ویرایش گردید' }));
        })
        .finally(() => setLoading(false));
    } else {
      addUser(name, lastName, email, password, mobileNumber)
        .then(() => {
          getAllUsers();
          closeDialog();
          dispatch(setSnackbar({ openSnackbar: true, message: 'کاربر با موفقیت اضافه گردید' }));
        })
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    if (password !== '') {
      if (
        password?.length > 4 &&
        !validateSpecialChar(password) &&
        !validateDigit(password) &&
        isContainsLowercase(password)
      )
        setPasswordError(false);
      else setPasswordError(true);
    }
  }, [password]);

  const disabled = !name || !lastName || !msisdn || emailError;

  return (
    <Dialog
      onClose={onCloseAndResetDialog}
      open={openAddUserDialog}
      maxWidth='xl'
      classes={{ paper: styles.paper }}
    >
      <Box className='mainDialog'>
        <Box className='header'>
          <Typography color='text.main' fontWeight='bold'>
            افزودن کاربر
          </Typography>
          <IconButton onClick={onCloseAndResetDialog}>
            <CloseRoundedIcon color='action' />
          </IconButton>
        </Box>

        <Grid container>
          <Grid item lg={4} md={6} sm={12} xs={12} p={1}>
            <CustomInput
              label={name ? 'نام' : ''}
              placeholder='نام'
              value={name}
              onChangeHandler={(value) => {
                const ignore = validateDigit(value);
                if (ignore) setName(value);
              }}
            />
          </Grid>

          <Grid item lg={4} md={6} xs={12} p={1}>
            <CustomInput
              label={lastName ? 'نام خانوادگی' : ''}
              placeholder='نام خانوادگی'
              value={lastName}
              onChangeHandler={(value) => {
                const ignore = validateDigit(value);
                if (ignore) setLastName(value);
              }}
            />
          </Grid>

          <Grid item lg={4} md={6} xs={12} p={1}>
            <CustomInput
              label={msisdn ? 'شماره همراه' : ''}
              placeholder='شماره همراه'
              value={msisdn}
              helperText={msisdnError ? 'شماره همراه اشتباه است.' : ''}
              maxLength={msisdn.startsWith('0') ? 11 : 10}
              error={msisdnError}
              inputMode='numeric'
              onChangeHandler={(value) => {
                setMsisdn(value);
                if (msisdnValidation(value) && value !== '') setMsisdnError(true);
                else setMsisdnError(false);
              }}
            />
          </Grid>

          <Grid item lg={8} md={6} xs={12} p={1}>
            <CustomInput
              label={email ? 'ایمیل' : ''}
              placeholder='ایمیل'
              value={email}
              onChangeHandler={(value) => {
                setEmail(value);

                if (!value && emailError) setEmailError(false);
                else {
                  if (emailValidation(value)) setEmailError(false);
                  else setEmailError(true);
                }
              }}
              error={emailError}
              helperText={emailError ? 'ایمیل وارد شده صحیح نمی‌باشد' : ''}
            />
          </Grid>

          <Grid item lg={4} md={6} xs={12} p={1}>
            <CustomInput
              label={password ? 'کلمه عبور' : ''}
              placeholder='کلمه عبور'
              value={password}
              type={type}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    {type === 'password' ? (
                      <IconButton onClick={() => setType('text')}>
                        <VisibilityOffIcon sx={{ color: '#6A6E73' }} />
                      </IconButton>
                    ) : (
                      <IconButton onClick={() => setType('password')}>
                        <VisibilityIcon sx={{ color: '#6A6E73' }} />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
              onChangeHandler={onChangePasswordHandler}
              error={passwordError}
              helperText={passwordError ? 'سطح امنیتی رمز عبور پایین می‌باشد' : ''}
            />
          </Grid>
        </Grid>

        <Box className='rowContainer'>
          <CustomButton variant='outlined' onClick={onCloseAndResetDialog}>
            <Box className='flexCenter' px={2}>
              <KeyboardArrowRightRoundedIcon color='primary' />
              <Typography>انصراف</Typography>
            </Box>
          </CustomButton>
          <CustomButton disabled={disabled || loading} loading={loading} onClick={onSubmit}>
            <Typography px={2}>{selectUser ? 'ویرایش' : 'تایید'}</Typography>
          </CustomButton>
        </Box>

        <Divider sx={{ marginTop: 2 }} />

        <UserBulkAccording
          file={file}
          setFile={setFile}
          fileLoading={fileLoading}
          onSubmitFile={onSubmitFile}
        />
      </Box>
    </Dialog>
  );
};

export default AddUserDialog;
